import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Checkbox,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Block,
  CheckCircle,
  LockReset,
  FilterList,
  Search,
  Person,
  AdminPanelSettings,
  Group,
  Business,
  Email,
  Phone,
  CalendarToday,
  Security,
  Visibility,
  VisibilityOff,
  ViewList,
  ViewModule
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { User, UserRole } from '../types';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Users: React.FC = () => {
  const { user, selectedOrganizationId } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Redirect ROOT_USER to root admin dashboard
  useEffect(() => {
    if (user?.role === UserRole.ROOT_USER) {
      navigate('/root-admin-dashboard', { replace: true });
    }
  }, [user?.role, navigate]);
  const [roleDialog, setRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>(UserRole.EMPLOYEE);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  
  // Debug dialog state changes
  useEffect(() => {
    console.log('🔍 Reset password dialog state changed:', resetPasswordDialog);
    if (resetPasswordDialog) {
      console.log('🔍 Dialog should be open, selectedUser:', selectedUser?.email);
    }
  }, [resetPasswordDialog, selectedUser]);
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    mustChangePassword: true
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  useEffect(() => {
    loadUsers();
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (user?.role === 'root_user') {
      loadOrganizations();
    }
  }, [user?.role]);

  // Filter users based on search and filter criteria
  useEffect(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.isActive : !user.isActive
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Organization filter
    if (organizationFilter !== 'all') {
      filtered = filtered.filter(user => user.organizationId === organizationFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter, roleFilter, organizationFilter]);

  const loadOrganizations = async () => {
    try {
      const response = await apiService.getOrganizations();
      console.log('🏢 Debug - Loaded organizations:', response);
      setOrganizations(response);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // ROOT_USER MUST select organization first - PRIVACY PROTECTION
      const isRootUser = user?.role === 'root_user';
      if (isRootUser && !selectedOrganizationId) {
        console.warn('🔒 ROOT_USER must select organization first - blocking data load for privacy');
        setUsers([]);
        setLoading(false);
        return; // BLOCK data loading until org selected
      }
      
      let data;
      if (isRootUser) {
        // Root user with selected organization
        data = await apiService.getUsersByOrganization(selectedOrganizationId!);
      } else {
        // Regular users get all users from their org
        data = await apiService.getAllUsers();
      }
      
      console.log('📥 Loaded users from API:', data.map(u => ({ 
        id: u.id, 
        email: u.email, 
        isActive: u.isActive,
        role: u.role 
      })));
      
      setUsers(data);
    } catch (error) {
      setError('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    console.log('Menu opened for user:', user.email, user.role);
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleUpdateRole = async () => {
    try {
      if (!selectedUser) {
        console.error('No user selected for role update');
        setError('No user selected');
        return;
      }
      
      console.log('Starting role update...');
      console.log('User ID:', selectedUser.id);
      console.log('Current role:', selectedUser.role);
      console.log('New role:', newRole);
      
      const updatedUser = await apiService.updateUserRole(selectedUser.id, newRole);
      console.log('Role update successful:', updatedUser);
      
      setRoleDialog(false);
      setNewRole(UserRole.EMPLOYEE);
      await loadUsers();
      setError(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role');
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!selectedUser) {
        setError('No user selected');
        return;
      }

      await apiService.resetPassword(
        selectedUser.id, 
        resetPasswordData.newPassword, 
        resetPasswordData.mustChangePassword
      );

      setResetPasswordDialog(false);
      setResetPasswordData({ newPassword: '', mustChangePassword: true });
      setError(null);
      
      // Show success message
      alert('Password reset successfully!');
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password');
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await apiService.updateUserStatus(userId, !currentStatus);
      await loadUsers();
      setError(null);
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Failed to update user status');
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ROOT_USER:
        return <AdminPanelSettings />;
      case UserRole.ADMIN:
        return <AdminPanelSettings />;
      case UserRole.SUPER_MANAGER:
        return <Group />;
      case UserRole.MANAGER:
        return <Group />;
      case UserRole.PROJECT_MANAGER:
        return <Business />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ROOT_USER:
        return 'error';
      case UserRole.ADMIN:
        return 'warning';
      case UserRole.SUPER_MANAGER:
        return 'info';
      case UserRole.MANAGER:
        return 'primary';
      case UserRole.PROJECT_MANAGER:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ROOT_USER:
        return 'Root User';
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.SUPER_MANAGER:
        return 'Super Manager';
      case UserRole.MANAGER:
        return 'Manager';
      case UserRole.PROJECT_MANAGER:
        return 'Project Manager';
      case UserRole.HR_ADMIN:
        return 'HR Admin';
      default:
        return 'Employee';
    }
  };

  const canManageUsers = user?.role && [
    UserRole.ADMIN,
    UserRole.SUPER_MANAGER,
    UserRole.ROOT_USER
  ].includes(user.role);

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        p: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user roles, passwords, and access permissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ViewList />}
              onClick={() => setViewMode('table')}
              color={viewMode === 'table' ? 'primary' : 'inherit'}
            >
              Table
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewModule />}
              onClick={() => setViewMode('cards')}
              color={viewMode === 'cards' ? 'primary' : 'inherit'}
            >
              Cards
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as any)}
                  label="Role"
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value={UserRole.ROOT_USER}>Root User</MenuItem>
                  <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
                  <MenuItem value={UserRole.SUPER_MANAGER}>Super Manager</MenuItem>
                  <MenuItem value={UserRole.MANAGER}>Manager</MenuItem>
                  <MenuItem value={UserRole.PROJECT_MANAGER}>Project Manager</MenuItem>
                  <MenuItem value={UserRole.HR_ADMIN}>HR Admin</MenuItem>
                  <MenuItem value={UserRole.EMPLOYEE}>Employee</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {user?.role === 'root_user' && (
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={organizationFilter}
                    onChange={(e) => setOrganizationFilter(e.target.value)}
                    label="Organization"
                  >
                    <MenuItem value="all">All Organizations</MenuItem>
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${filteredUsers.length} users`}
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setRoleFilter('all');
                    setOrganizationFilter('all');
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Users List */}
        {filteredUsers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Users Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' || organizationFilter !== 'all'
                ? 'Try adjusting your filters to see more users.'
                : 'Users are automatically created when you add employees. Go to the Employees page to create new team members.'}
            </Typography>
            {!searchTerm && statusFilter === 'all' && roleFilter === 'all' && organizationFilter === 'all' && (
              <Button
                variant="contained"
                startIcon={<Person />}
                onClick={() => window.location.href = '/employees'}
              >
                Go to Employees Page
              </Button>
            )}
          </Paper>
        ) : viewMode === 'table' ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          {user.title && (
                            <Typography variant="caption" color="text.secondary">
                              {user.title}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {organizations.find(org => org.id === user.organizationId)?.name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {canManageUsers && (
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, user)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Grid container spacing={3}>
            {filteredUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                      {canManageUsers && (
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, user)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role) as any}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    {user.title && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <Business sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        {user.title}
                      </Typography>
                    )}
                    
                    {user.department && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <Group sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        {user.department}
                      </Typography>
                    )}
                    
                    <Typography variant="body2" color="text.secondary">
                      <CalendarToday sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      Last login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            setNewRole(selectedUser?.role || UserRole.EMPLOYEE);
            setRoleDialog(true);
            setAnchorEl(null); // Close menu but keep selectedUser
          }}>
            <Edit sx={{ mr: 1 }} />
            Change Role
          </MenuItem>
          <MenuItem onClick={() => {
            console.log('🔍 Reset Password clicked for user:', selectedUser?.email);
            setResetPasswordDialog(true);
            setAnchorEl(null); // Close menu but keep selectedUser
          }}>
            <LockReset sx={{ mr: 1 }} />
            Reset Password
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedUser) {
              handleToggleUserStatus(selectedUser.id, selectedUser.isActive);
            }
            handleMenuClose();
          }}>
            {selectedUser?.isActive ? (
              <>
                <Block sx={{ mr: 1 }} />
                Deactivate User
              </>
            ) : (
              <>
                <CheckCircle sx={{ mr: 1 }} />
                Activate User
              </>
            )}
          </MenuItem>
        </Menu>

        {/* Role Update Dialog */}
        <Dialog open={roleDialog} onClose={() => {
          setRoleDialog(false);
          setSelectedUser(null); // Clear selectedUser when dialog closes
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Change User Role</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current role: <strong>{selectedUser && getRoleLabel(selectedUser.role)}</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>New Role</InputLabel>
                <Select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as UserRole)}
                  label="New Role"
                >
                  <MenuItem value={UserRole.EMPLOYEE}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person />
                      Employee - Basic access
                    </Box>
                  </MenuItem>
                  <MenuItem value={UserRole.MANAGER}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group />
                      Manager - Team management
                    </Box>
                  </MenuItem>
                  <MenuItem value={UserRole.SUPER_MANAGER}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group />
                      Super Manager - Projects, Timesheets, Leaves
                    </Box>
                  </MenuItem>
                  <MenuItem value={UserRole.PROJECT_MANAGER}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business />
                      Project Manager - Project management
                    </Box>
                  </MenuItem>
                  <MenuItem value={UserRole.HR_ADMIN}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AdminPanelSettings />
                      HR Admin - HR management
                    </Box>
                  </MenuItem>
                  <MenuItem value={UserRole.ADMIN}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AdminPanelSettings />
                      Admin - Full system access
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} variant="contained">
              Update Role
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog 
          open={resetPasswordDialog} 
          onClose={() => {
            console.log('🔍 Closing reset password dialog');
            setResetPasswordDialog(false);
            setSelectedUser(null); // Clear selectedUser when dialog closes
          }} 
          maxWidth="sm" 
          fullWidth
          sx={{ zIndex: 9999 }}
        >
          <DialogTitle>Reset User Password</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Resetting password for: <strong>{selectedUser?.email}</strong>
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={resetPasswordData.newPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resetPasswordData.mustChangePassword}
                    onChange={(e) => setResetPasswordData({ 
                      ...resetPasswordData, 
                      mustChangePassword: e.target.checked 
                    })}
                  />
                }
                label="Force user to change password on next login"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPasswordDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleResetPassword} 
              variant="contained"
              disabled={!resetPasswordData.newPassword}
            >
              Reset Password
            </Button>
          </DialogActions>
        </Dialog>
    </Layout>
  );
};

export default Users;